import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
// @ts-ignore
import AOS from 'aos';
import 'aos/dist/aos.css';
import { useWebsiteFeatures, useCookieConsent } from '../../hooks/useWebsiteFeatures';

// Facebook Pixel declaration
declare global {
  interface Window {
    fbq: (...args: any[]) => void;
  }
}

const HomePage: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [cookieConsent, setCookieConsent] = useState<string | null>(null);

  // Use custom hooks
  const { smoothScrollTo, trackDownload } = useWebsiteFeatures();
  const { acceptCookies: acceptCookiesHook, rejectCookies: rejectCookiesHook } = useCookieConsent();

  useEffect(() => {
    // Initialize AOS
    AOS.init({
      duration: 800,
      easing: 'ease-out-cubic',
      once: true,
      offset: 100
    });

    // Facebook Pixel - ViewContent event
    if (typeof window.fbq === 'function') {
      window.fbq('track', 'ViewContent', {
        content_name: 'Homepage',
        content_category: 'Landing Page'
      });
    }

    // Check cookie consent
    const consent = localStorage.getItem('cookieConsent');
    setCookieConsent(consent);

    // Show cookie consent after 2 seconds if not set
    if (!consent) {
      setTimeout(() => {
        setCookieConsent('show');
      }, 2000);
    }

    // Navbar scroll effect
    const handleScroll = () => {
      const navbar = document.querySelector('.navbar');
      if (navbar) {
        if (window.scrollY > 100) {
          navbar.classList.add('scrolled');
        } else {
          navbar.classList.remove('scrolled');
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleSmoothScroll = (e: React.MouseEvent<HTMLAnchorElement>, targetId: string) => {
    e.preventDefault();
    smoothScrollTo(targetId);
    setMobileMenuOpen(false);
  };

  const acceptCookies = () => {
    acceptCookiesHook();
    setCookieConsent('accepted');
  };

  const rejectCookies = () => {
    rejectCookiesHook();
    setCookieConsent('rejected');
  };

  const handleDownloadClick = (platform: string) => {
    trackDownload(platform);
  };

  return (
    <div className="website-container">
      {/* Navigation */}
      <nav className="navbar">
        <div className="nav-container">
          <div className="nav-logo">
            <a href="/" className="logo-link">
              <img src="/assets/LogoBrown.png" alt="SobCof Logo" className="logo-img" />
            </a>
          </div>
          <div className={`nav-menu ${mobileMenuOpen ? 'active' : ''}`}>
            <a href="#home" className="nav-link" onClick={(e) => handleSmoothScroll(e, '#home')}>Ana Sayfa</a>
            <a href="#features" className="nav-link" onClick={(e) => handleSmoothScroll(e, '#features')}>Özellikler</a>
            <a href="#how-it-works" className="nav-link" onClick={(e) => handleSmoothScroll(e, '#how-it-works')}>Nasıl Çalışır</a>
            <a href="#download" className="nav-link" onClick={(e) => handleSmoothScroll(e, '#download')}>İndir</a>
            <a href="/contact" className="nav-link">İletişim</a>
            <a href="/cafe-partnership" className="nav-link">Kafe Partnerliği</a>
          </div>
          <div className="nav-toggle" onClick={toggleMobileMenu}>
            <i className={`fas ${mobileMenuOpen ? 'fa-times' : 'fa-bars'}`}></i>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="hero">
        <div className="hero-container">
          <motion.div 
            className="hero-content"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <motion.h1 
              className="hero-title"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Kahve Severler İçin
              <span className="highlight"> Sosyal Keşif</span> Platformu
            </motion.h1>
            <motion.p 
              className="hero-description"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              En iyi kafeleri keşfet, çevrendeki kahve tutkunlarıyla bağlantı kur ve yeni insanlarla tanış.
            </motion.p>
            <motion.div 
              className="hero-buttons"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <motion.a 
                href="#download" 
                className="btn-primary" 
                onClick={(e) => handleSmoothScroll(e, '#download')}
                whileHover={{ scale: 1.05, translateY: -3 }}
                whileTap={{ scale: 0.95 }}
              >
                <i className="fab fa-apple"></i>
                App Store'dan İndir
              </motion.a>
              <motion.a 
                href="#how-it-works" 
                className="btn-secondary" 
                onClick={(e) => handleSmoothScroll(e, '#how-it-works')}
                whileHover={{ scale: 1.05, translateY: -3 }}
                whileTap={{ scale: 0.95 }}
              >
                <i className="fas fa-play"></i>
                Nasıl Çalışır?
              </motion.a>
            </motion.div>
          </motion.div>
          
          <motion.div 
            className="hero-visual"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3, type: "spring", stiffness: 60 }}
          >
            <div className="phone-mockup">
              <img src="/assets/herosectionphone.png" alt="" className="phone-img" data-nosnippet />
              <div className="floating-elements">
                <motion.div 
                  className="floating-card coffee"
                  initial={{ opacity: 0, scale: 0.5, y: 50 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{ 
                    delay: 0.6,
                    type: "spring",
                    stiffness: 80
                  }}
                >
                  <motion.div
                    animate={{ y: [0, -10, 0] }}
                    transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                  >
                    <i className="fas fa-coffee"></i>
                    <span>Cappuccino Seviyorum</span>
                  </motion.div>
                </motion.div>
                
                <motion.div 
                  className="floating-card match"
                  initial={{ opacity: 0, scale: 0.5, y: 50 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{ 
                    delay: 0.8,
                    type: "spring",
                    stiffness: 80
                  }}
                >
                  <motion.div
                    animate={{ y: [0, 12, 0] }}
                    transition={{ repeat: Infinity, duration: 4.5, ease: "easeInOut", delay: 0.3 }}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                  >
                    <i className="fas fa-heart"></i>
                    <span>Yeni Eşleşme!</span>
                  </motion.div>
                </motion.div>
                
                <motion.div 
                  className="floating-card location"
                  initial={{ opacity: 0, scale: 0.5, y: 50 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{ 
                    delay: 1.0,
                    type: "spring",
                    stiffness: 80
                  }}
                >
                  <motion.div
                    animate={{ y: [0, -8, 0] }}
                    transition={{ repeat: Infinity, duration: 5, ease: "easeInOut", delay: 0.6 }}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                  >
                    <i className="fas fa-map-marker-alt"></i>
                    <span>Starbucks Taksim</span>
                  </motion.div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
           {/* Features Section */}
      <section id="features" className="features">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="section-title">Neden SobCof?</h2>
            <p className="section-subtitle">Kahve severler için tasarlanmış benzersiz özellikleri keşfet</p>
          </motion.div>

          <motion.div 
            className="features-grid"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={{
              hidden: {},
              visible: {
                transition: {
                  staggerChildren: 0.1
                }
              }
            }}
          >
            <motion.div 
              className="feature-card glass-card"
              variants={{
                hidden: { opacity: 0, y: 30 },
                visible: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 100 } }
              }}
              whileHover={{ y: -8, scale: 1.02 }}
            >
              <div className="feature-icon">
                <i className="fas fa-map-marked-alt"></i>
              </div>
              <h3 className="feature-title">Gerçek Kafe Konumları</h3>
              <p className="feature-description">
                Sadece gerçek kafelerde bulunan kişilerle eşleşin. Google Places entegrasyonu ile doğrulanmış lokasyonlar.
              </p>
            </motion.div>

            <motion.div 
              className="feature-card glass-card"
              variants={{
                hidden: { opacity: 0, y: 30 },
                visible: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 100 } }
              }}
              whileHover={{ y: -8, scale: 1.02 }}
            >
              <div className="feature-icon">
                <i className="fas fa-users"></i>
              </div>
              <h3 className="feature-title">Akıllı Öneri Sistemi</h3>
              <p className="feature-description">
                Kahve tercihleriniz ve ilgi alanlarınıza göre size en uygun kafeleri ve insanları önerir.
              </p>
            </motion.div>

            <motion.div 
              className="feature-card glass-card"
              variants={{
                hidden: { opacity: 0, y: 30 },
                visible: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 100 } }
              }}
              whileHover={{ y: -8, scale: 1.02 }}
            >
              <div className="feature-icon">
                <i className="fas fa-user-secret"></i>
              </div>
              <h3 className="feature-title">Gizlilik Öncelikli</h3>
              <p className="feature-description">
                Önce anonim sohbet et, rahat hissettiğinde profilini paylaş. Kontrol tamamen sende.
              </p>
            </motion.div>

            <motion.div 
              className="feature-card glass-card"
              variants={{
                hidden: { opacity: 0, y: 30 },
                visible: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 100 } }
              }}
              whileHover={{ y: -8, scale: 1.02 }}
            >
              <div className="feature-icon">
                <i className="fas fa-comments"></i>
              </div>
              <h3 className="feature-title">Gerçek Zamanlı Sohbet</h3>
              <p className="feature-description">
                Anlık mesajlaşma, fotoğraf paylaşımı ve ses mesajları ile zengin iletişim deneyimi.
              </p>
            </motion.div>

            <motion.div 
              className="feature-card glass-card"
              variants={{
                hidden: { opacity: 0, y: 30 },
                visible: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 100 } }
              }}
              whileHover={{ y: -8, scale: 1.02 }}
            >
              <div className="feature-icon">
                <i className="fas fa-filter"></i>
              </div>
              <h3 className="feature-title">Gelişmiş Filtreler</h3>
              <p className="feature-description">
                Yaş, mesafe, kafe tercihi ve kahve türleri gibi detaylı filtrelerle istediğiniz kişileri bulun.
              </p>
            </motion.div>

            <motion.div 
              className="feature-card glass-card"
              variants={{
                hidden: { opacity: 0, y: 30 },
                visible: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 100 } }
              }}
              whileHover={{ y: -8, scale: 1.02 }}
            >
              <div className="feature-icon">
                <i className="fas fa-shield-alt"></i>
              </div>
              <h3 className="feature-title">Güvenli Ortam</h3>
              <p className="feature-description">
                Kapsamlı doğrulama sistemi, engelleme ve raporlama özellikleri ile güvenli deneyim.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="how-it-works">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="section-title">Nasıl Çalışır?</h2>
            <p className="section-subtitle">4 basit adımda yeni insanlarla tanış</p>
          </motion.div>

          <div className="steps-container">
            <motion.div 
              className="step"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.7, type: "spring" }}
            >
              <div className="step-number">1</div>
              <div className="step-content glass-card">
                <h3 className="step-title">Kafeye Gidin</h3>
                <p className="step-description">
                  Sevdiğiniz bir kafeye gidin ve SobCof uygulamasını açın. Konumunuz otomatik olarak tespit edilecek.
                </p>
              </div>
              <motion.div 
                className="step-visual"
                whileHover={{ scale: 1.08 }}
              >
                <img src="/assets/step1.png" alt="Kafeye Git" />
              </motion.div>
            </motion.div>

            <motion.div 
              className="step step-reverse"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.7, type: "spring" }}
            >
              <motion.div 
                className="step-visual"
                whileHover={{ scale: 1.08 }}
              >
                <img src="/assets/step2.png" alt="Eşleşme" />
              </motion.div>
              <div className="step-content glass-card">
                <h3 className="step-title">Keşfet</h3>
                <p className="step-description">
                  Aynı kafedeki diğer SobCof kullanıcılarını gör ve ilgini çekenleri sağa kaydır.
                </p>
              </div>
              <div className="step-number">2</div>
            </motion.div>

            <motion.div 
              className="step"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.7, type: "spring" }}
            >
              <div className="step-number">3</div>
              <div className="step-content glass-card">
                <h3 className="step-title">Anonim Sohbet</h3>
                <p className="step-description">
                  Karşılıklı beğeni olduğunda anonim sohbet başlar. Birbirinizi tanıyın ve uyumunuzu test edin.
                </p>
              </div>
              <motion.div 
                className="step-visual"
                whileHover={{ scale: 1.08 }}
              >
                <img src="/assets/step3.png" alt="Anonim Sohbet" />
              </motion.div>
            </motion.div>

            <motion.div 
              className="step step-reverse"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.7, type: "spring" }}
            >
              <motion.div 
                className="step-visual"
                whileHover={{ scale: 1.08 }}
              >
                <img src="/assets/step4.png" alt="Tanışma" />
              </motion.div>
              <div className="step-content glass-card">
                <h3 className="step-title">Buluş</h3>
                <p className="step-description">
                  Hazır olduğunuzda profillerinizi paylaşın ve kahvenizi birlikte için!
                </p>
              </div>
              <div className="step-number">4</div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Premium Section */}
      <section className="premium">
        <div className="container">
          <motion.div 
            className="premium-content"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
          >
            <div className="premium-header">
              <motion.div 
                className="premium-icon"
                animate={{ rotate: [0, 5, -5, 0], scale: [1, 1.05, 1] }}
                transition={{ repeat: Infinity, duration: 6, ease: "easeInOut" }}
              >
                <i className="fas fa-crown"></i>
              </motion.div>
              <h2 className="premium-title">SobCof Premium</h2>
              <p className="premium-subtitle">Daha fazla özellik, daha fazla keşif</p>
            </div>

            <motion.div 
              className="premium-features"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={{
                hidden: {},
                visible: {
                  transition: {
                    staggerChildren: 0.08
                  }
                }
              }}
            >
              <motion.div 
                className="premium-feature"
                variants={{
                  hidden: { opacity: 0, scale: 0.9 },
                  visible: { opacity: 1, scale: 1, transition: { type: "spring" } }
                }}
                whileHover={{ y: -8, backgroundColor: "rgba(255, 255, 255, 0.15)", borderColor: "rgba(255, 215, 0, 0.4)" }}
              >
                <i className="fas fa-infinity"></i>
                <span>Sınırsız Beğeni</span>
              </motion.div>
              
              <motion.div 
                className="premium-feature"
                variants={{
                  hidden: { opacity: 0, scale: 0.9 },
                  visible: { opacity: 1, scale: 1, transition: { type: "spring" } }
                }}
                whileHover={{ y: -8, backgroundColor: "rgba(255, 255, 255, 0.15)", borderColor: "rgba(255, 215, 0, 0.4)" }}
              >
                <i className="fas fa-eye"></i>
                <span>Sizi Beğenenleri Görün</span>
              </motion.div>
              
              <motion.div 
                className="premium-feature"
                variants={{
                  hidden: { opacity: 0, scale: 0.9 },
                  visible: { opacity: 1, scale: 1, transition: { type: "spring" } }
                }}
                whileHover={{ y: -8, backgroundColor: "rgba(255, 255, 255, 0.15)", borderColor: "rgba(255, 215, 0, 0.4)" }}
              >
                <i className="fas fa-undo"></i>
                <span>Geri Alma Özelliği</span>
              </motion.div>
              
              <motion.div 
                className="premium-feature"
                variants={{
                  hidden: { opacity: 0, scale: 0.9 },
                  visible: { opacity: 1, scale: 1, transition: { type: "spring" } }
                }}
                whileHover={{ y: -8, backgroundColor: "rgba(255, 255, 255, 0.15)", borderColor: "rgba(255, 215, 0, 0.4)" }}
              >
                <i className="fas fa-filter"></i>
                <span>Gelişmiş Filtreler</span>
              </motion.div>
              
              <motion.div 
                className="premium-feature"
                variants={{
                  hidden: { opacity: 0, scale: 0.9 },
                  visible: { opacity: 1, scale: 1, transition: { type: "spring" } }
                }}
                whileHover={{ y: -8, backgroundColor: "rgba(255, 255, 255, 0.15)", borderColor: "rgba(255, 215, 0, 0.4)" }}
              >
                <i className="fas fa-map-marker-alt"></i>
                <span>Genişletilmiş Mesafe</span>
              </motion.div>
              
              <motion.div 
                className="premium-feature"
                variants={{
                  hidden: { opacity: 0, scale: 0.9 },
                  visible: { opacity: 1, scale: 1, transition: { type: "spring" } }
                }}
                whileHover={{ y: -8, backgroundColor: "rgba(255, 255, 255, 0.15)", borderColor: "rgba(255, 215, 0, 0.4)" }}
              >
                <i className="fas fa-user-secret"></i>
                <span>Gizli Mod</span>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Download Section */}
      <section id="download" className="download">
        <div className="container">
          <motion.div 
            className="download-content"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7 }}
          >
            <h2 className="download-title">SobCof'u İndirin</h2>
            <p className="download-subtitle">
              Kahve severlerle bağlantı kurmaya bugün başla
            </p>

            <div className="download-buttons">
              <motion.a
                href="https://apps.apple.com/tr/app/sobcof/id6751366978?l=tr"
                className="download-btn ios"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => handleDownloadClick('ios')}
                whileHover={{ scale: 1.05, y: -4 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="download-btn-icon">
                  <i className="fab fa-apple"></i>
                </div>
                <div className="download-btn-text">
                  <span className="download-label">Download on the</span>
                  <span className="download-store">App Store</span>
                </div>
              </motion.a>

              <motion.a
                href="https://play.google.com/store/apps/details?id=com.sobcof.sobcofandroid"
                className="download-btn android"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => handleDownloadClick('android')}
                whileHover={{ scale: 1.05, y: -4 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="download-btn-icon">
                  <i className="fab fa-google-play"></i>
                </div>
                <div className="download-btn-text">
                  <span className="download-label">GET IT ON</span>
                  <span className="download-store">Google Play</span>
                </div>
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <div className="footer-logo">
                <a href="/" className="logo-link">
                  <img src="/assets/LogoWhite.png" alt="SobCof Logo" />
                </a>
              </div>
              <p className="footer-description">
                Kahve severler için sosyal keşif platformu. En iyi kafeleri keşfet, yeni insanlarla tanış.
              </p>
              <div className="social-links">
                <a href="https://instagram.com/sobcof" title="Instagram" target="_blank" rel="noopener noreferrer"><i className="fab fa-instagram"></i></a>
                <a href="https://tiktok.com/@sobcof" title="TikTok" target="_blank" rel="noopener noreferrer"><i className="fab fa-tiktok"></i></a>
                <a href="https://twitter.com/sobcof" title="Twitter" target="_blank" rel="noopener noreferrer"><i className="fab fa-twitter"></i></a>
                <a href="https://youtube.com/@sobcof" title="YouTube" target="_blank" rel="noopener noreferrer"><i className="fab fa-youtube"></i></a>
              </div>
            </div>

            <div className="footer-section">
              <h4>Ürün</h4>
              <ul>
                <li><a href="#features" onClick={(e) => handleSmoothScroll(e, '#features')}>Özellikler</a></li>
                <li><a href="#premium" onClick={(e) => handleSmoothScroll(e, '#premium')}>Premium</a></li>
                <li><a href="#download" onClick={(e) => handleSmoothScroll(e, '#download')}>İndir</a></li>
              </ul>
            </div>

            <div className="footer-section">
              <h4>Destek</h4>
              <ul>
                <li><a href="/help">Yardım Merkezi</a></li>
                <li><a href="/contact">İletişim</a></li>
                <li><a href="/feedback">Geri Bildirim</a></li>
              </ul>
            </div>

            <div className="footer-section">
              <h4>Yasal</h4>
              <ul>
                <li><a href="/privacy">Gizlilik Politikası</a></li>
                <li><a href="/terms">Kullanım Koşulları</a></li>
                <li><a href="/cookies">Çerez Politikası</a></li>
                <li><a href="/gdpr">KVKK</a></li>
              </ul>
            </div>
          </div>

          <div className="footer-bottom">
            <p>&copy; 2026 SobCof. Tüm hakları saklıdır.</p>
          </div>
        </div>
      </footer>

      {/* Cookie Consent */}
      {cookieConsent === 'show' && (
        <div id="cookie-consent" className="cookie-consent show">
          <div className="cookie-content">
            <p>
              Web sitemizde deneyiminizi geliştirmek için çerezler kullanıyoruz.
              Detaylı bilgi için <a href="/cookies">çerez politikamızı</a> inceleyebilirsiniz.
            </p>
            <div className="cookie-buttons">
              <button onClick={acceptCookies} className="cookie-btn accept">Kabul Et</button>
              <button onClick={rejectCookies} className="cookie-btn reject">Reddet</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;
